import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Delete } from "lucide-react";

type PinPadProps = {
  onSubmit: (pin: string) => void;
  disabled?: boolean;
  errorMessage?: string | null;
};

const MAX_LEN = 12;

export function PinPad({ onSubmit, disabled, errorMessage }: PinPadProps) {
  const { t } = useTranslation("common");
  const [pin, setPin] = useState("");

  const append = (d: string) => {
    if (disabled || pin.length >= MAX_LEN) return;
    setPin((p) => p + d);
  };

  const backspace = () => {
    if (disabled) return;
    setPin((p) => p.slice(0, -1));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled || pin.length < 4) return;
    onSubmit(pin);
  };

  return (
    <Card className="w-full max-w-sm p-6 space-y-6 mx-auto">
      <div className="text-center space-y-1">
        <h2 className="text-lg font-semibold">{t("auth.pinTitle")}</h2>
        <p className="text-sm text-muted-foreground">{t("auth.pinSubtitle")}</p>
      </div>

      <div
        className="text-center text-2xl font-mono tracking-[0.4em] min-h-[2.5rem] border rounded-md py-2 bg-muted/40"
        aria-live="polite"
      >
        {pin ? "•".repeat(pin.length) : "—"}
      </div>

      {errorMessage ? (
        <p className="text-sm text-destructive text-center">{errorMessage}</p>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-3 gap-2">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "⌫"].map(
            (key) => (
              <Button
                key={key}
                type="button"
                variant={key === "C" ? "outline" : "secondary"}
                className="h-12 text-lg"
                disabled={disabled}
                onClick={() => {
                  if (key === "⌫") backspace();
                  else if (key === "C") setPin("");
                  else append(key);
                }}
              >
                {key === "⌫" ? <Delete className="h-5 w-5 mx-auto" /> : key}
              </Button>
            )
          )}
        </div>
        <Button
          type="submit"
          className="w-full"
          disabled={disabled || pin.length < 4}
        >
          {t("auth.pinContinue")}
        </Button>
      </form>
    </Card>
  );
}
